import { globalEnv } from "~/global-env";
import { TaxDataSourceAssignmentStatus } from "~/graphql/supergraphEnums";
import {
  type GetComplianceCaseQuery,
  type GetTaxDataSourceAssignmentsQuery,
} from "~/graphql/supergraphTypes";
import {
  getDocumentCategory,
  getSecondaryDocumentType,
  getTaxFormTypeFromDocument,
  SELF_CERTIFICATION_BASE_TYPE,
  W_SERIES_BASE_TYPE,
} from "../Documents/TaxForms/utils/documentCategoryHelpers";
import { taxReportingRowToControllingPersonEntry } from "../Documents/TaxForms/utils/taxReportingProfileSync";
import { type TaxReportingDataItem } from "../FatcaCrs/types";
import { getFullLegalName, getInvestingProfileName } from "../Profiles/utils";
import type { AddressValues, ControllingPersonEntry } from "../Documents/TaxForms/types";

export type { ControllingPersonEntry };

export type TaxPerson = {
  id: string;
  name: string;
  residenceAddress: string | null;
  tin: string | null;
  controllingPersonType: string | null;
};

const formatPersonAddress = (address: AddressValues | null | undefined): string | null => {
  if (!address) return null;
  const cityStateZip = [address.city, address.stateOrProvince, address.zipCode]
    .filter(Boolean)
    .join(", ");
  const parts = [
    address.streetLine1,
    address.streetLine2,
    cityStateZip,
    address.country,
  ].filter(Boolean);
  return parts.length > 0 ? parts.join("\n") : null;
};

export const controllingPersonToTaxPerson = (entry: ControllingPersonEntry): TaxPerson => ({
  id: entry.id,
  name: getFullLegalName(entry.firstName, entry.lastName, entry.middleName),
  residenceAddress: formatPersonAddress(entry.address),
  tin: entry.tin ?? null,
  controllingPersonType: entry.controllingPersonType ?? null,
});

export type ComplianceCaseData = NonNullable<GetComplianceCaseQuery["complianceCase"]>;
export type ComplianceCaseDocument = NonNullable<ComplianceCaseData["documents"]>[number];
type TaxAssignment = NonNullable<
  GetTaxDataSourceAssignmentsQuery["taxDataSourceAssignments"]
>[number];

export type TaxOverviewVariant = "fatca" | "crs";

export type SourceDocumentInfo = {
  documentId: number;
  name: string;
  expiryDate: string | null;
};

export type FatcaOverviewRow = {
  id: string;
  rootProfileId: number;
  rootProfileName: string;
  positionsLabel: string;
  sourceDocument: SourceDocumentInfo | null;
  persons: ControllingPersonEntry[];
};

export type CrsOverviewRow = {
  id: string;
  positionLabel: string;
  rootProfileId: number;
  rootProfileName: string;
  positionCaseId: number;
  sourceDocument: SourceDocumentInfo | null;
  persons: ControllingPersonEntry[];
};

export type CasePosition = {
  caseId: number | null | undefined;
  entityName: string | null | undefined;
  rootProfileName: string | null | undefined;
};

const FATCA_PERSON_TYPE = "fatca_substantial_owner";
const CRS_PERSON_TYPE = "crs_controlling_person";
const CRS_ASSIGNMENT = "crs";
const FATCA_ASSIGNMENT = "fatca";

const collectPersons = (
  taxReportingData: TaxReportingDataItem[],
  reportingType: string,
  sourceDocumentId: number | null,
): ControllingPersonEntry[] => {
  if (sourceDocumentId === null) return [];
  return taxReportingData
    .filter(
      (item) =>
        item.assigned === true &&
        item.taxReportingType === reportingType &&
        item.documentId === sourceDocumentId,
    )
    .map(taxReportingRowToControllingPersonEntry);
};

const toSourceDocument = (doc: ComplianceCaseDocument | null): SourceDocumentInfo | null =>
  doc ? { documentId: doc.id, name: doc.name, expiryDate: doc.expiryDate ?? null } : null;

const findFatcaSourceDocument = (
  documents: ComplianceCaseDocument[],
  assignments: TaxAssignment[],
  rootProfileId: number,
): ComplianceCaseDocument | null => {
  const documentIds = assignments
    .filter(
      (a) =>
        a.assignmentType === FATCA_ASSIGNMENT &&
        a.profileId === rootProfileId &&
        a.status === TaxDataSourceAssignmentStatus.Assigned,
    )
    .map((a) => a.documentId);
  if (documentIds.length === 0) return null;
  return (
    documents.find(
      (d) => documentIds.includes(d.id) && d.documentPurpose?.baseType === W_SERIES_BASE_TYPE,
    ) ?? null
  );
};

const findCrsSourceDocumentForPosition = (
  documents: ComplianceCaseDocument[],
  assignments: TaxAssignment[],
  positionCaseId: number,
  rootProfileId: number,
): ComplianceCaseDocument | null => {
  const documentIds = assignments
    .filter(
      (a) =>
        a.assignmentType === CRS_ASSIGNMENT &&
        a.profileId === rootProfileId &&
        a.referencedCaseId === positionCaseId &&
        a.status === TaxDataSourceAssignmentStatus.Assigned,
    )
    .map((a) => a.documentId);
  if (documentIds.length === 0) return null;
  return (
    documents.find(
      (d) =>
        documentIds.includes(d.id) &&
        d.documentPurpose?.baseType === SELF_CERTIFICATION_BASE_TYPE,
    ) ?? null
  );
};

export type BuildCrsOverviewParams = {
  complianceCase: ComplianceCaseData;
  positions: CasePosition[];
  taxReportingData: TaxReportingDataItem[];
  assignments: TaxAssignment[];
};

export type BuildFatcaOverviewParams = {
  complianceCase: ComplianceCaseData;
  taxReportingData: TaxReportingDataItem[];
  assignments: TaxAssignment[];
};

export const buildFatcaOverviewRow = ({
  complianceCase,
  taxReportingData,
  assignments,
}: BuildFatcaOverviewParams): FatcaOverviewRow | null => {
  const root = complianceCase.investingProfile;
  if (!root) return null;
  const sourceDoc = findFatcaSourceDocument(
    complianceCase.documents ?? [],
    assignments,
    root.id,
  );
  const sourceDocument = toSourceDocument(sourceDoc);
  return {
    id: `fatca-${root.id}`,
    rootProfileId: root.id,
    rootProfileName: getInvestingProfileName(root),
    positionsLabel: "All positions",
    sourceDocument,
    persons: collectPersons(
      taxReportingData,
      FATCA_PERSON_TYPE,
      sourceDocument?.documentId ?? null,
    ),
  };
};

export const buildCrsOverviewRows = ({
  complianceCase,
  positions,
  taxReportingData,
  assignments,
}: BuildCrsOverviewParams): CrsOverviewRow[] => {
  const root = complianceCase.investingProfile;
  if (!root) return [];
  const rootName = getInvestingProfileName(root);
  const documents = complianceCase.documents ?? [];

  return positions
    .filter((p): p is CasePosition & { caseId: number } => typeof p.caseId === "number")
    .map((position) => {
      const sourceDoc = findCrsSourceDocumentForPosition(
        documents,
        assignments,
        position.caseId,
        root.id,
      );
      const sourceDocument = toSourceDocument(sourceDoc);
      return {
        id: `crs-${position.caseId}`,
        positionLabel: position.entityName ? `${rootName} in ${position.entityName}` : rootName,
        rootProfileId: root.id,
        rootProfileName: rootName,
        positionCaseId: position.caseId,
        sourceDocument,
        persons: collectPersons(
          taxReportingData,
          CRS_PERSON_TYPE,
          sourceDocument?.documentId ?? null,
        ),
      };
    });
};

const caseBasePath = (caseId: number): string => `${globalEnv.complianceRoute}/case/${caseId}`;

export const buildDocumentManagementUrl = (caseId: number, documentId: number): string =>
  `${caseBasePath(caseId)}/document-management?documentId=${documentId}`;

export const buildTaxFormUrlForDocument = (
  caseId: number,
  document: ComplianceCaseDocument | null,
  rootProfileId: number,
): string | null => {
  if (!document) return null;
  const formType = getTaxFormTypeFromDocument(
    getDocumentCategory(document.documentPurpose?.baseType),
    getSecondaryDocumentType(document.metadata),
  );
  if (!formType) return null;
  const params = new URLSearchParams({
    formType,
    documentId: String(document.id),
    profileId: String(rootProfileId),
  });
  return `${caseBasePath(caseId)}/document-management/tax-form?${params.toString()}`;
};
